/**
 * ShareLeaderboard — "Most-shared this week" panel for the user Dashboard.
 * Pulls from /api/share-stats/leaderboard and shows the top posts with
 * their per-network share breakdown (WhatsApp / X / Facebook / …).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { TrendingUp, Share2, Trophy } from "lucide-react";

const NETWORK_LABELS = {
  whatsapp: "WhatsApp",
  twitter: "X",
  facebook: "Facebook",
  linkedin: "LinkedIn",
  slack: "Slack",
  telegram: "Telegram",
  discord: "Discord",
  pinterest: "Pinterest",
  reddit: "Reddit",
  instagram: "Instagram",
  email: "Email",
  direct: "Direct",
};

const NETWORK_COLORS = {
  whatsapp: "#25D366",
  twitter: "#111",
  facebook: "#1877F2",
  linkedin: "#0A66C2",
  slack: "#4A154B",
  telegram: "#0088CC",
  discord: "#5865F2",
  pinterest: "#E60023",
  reddit: "#FF4500",
  instagram: "#E4405F",
  email: "#6b6b6b",
  direct: "#B25D38",
};

const TYPE_LINKS = {
  causes: (d) => `/causes/${d.slug || d.id}`,
  appeals: (d) => `/offer-help/${d.slug || d.id}`,
  help_offers: (d) => `/find-help/${d.slug || d.id}`,
  family_trees: (d) => `/family/${d.slug || d.id}`,
  sp_articles: (d) => `/articles/${d.slug}`,
  events: (d) => `/events/${d.slug || d.id}`,
  memorial_nominations: (d) => `/celebrate/${d.slug || d.id}`,
};

const TYPE_LABELS = {
  causes: "Cause",
  appeals: "Appeal",
  help_offers: "Help offer",
  family_trees: "Family",
  sp_articles: "Article",
  events: "Event",
  memorial_nominations: "Memorial",
};

export default function ShareLeaderboard({ days = 7, limit = 6 }) {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let alive = true;
    Promise.all([
      http.get(`/share-stats/leaderboard?days=${days}&limit=${limit}`),
      http.get(`/share-stats/summary?days=${days}`),
    ])
      .then(([lb, sm]) => { if (alive) { setRows(lb.data.items || []); setSummary(sm.data); } })
      .catch(() => { if (alive) { setRows([]); setSummary(null); } });
    return () => { alive = false; };
  }, [days, limit]);

  if (rows === null) {
    return (
      <section className="wofy-card mt-6" data-testid="share-leaderboard-loading">
        <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading share activity…</p>
      </section>
    );
  }
  if (rows.length === 0) {
    return (
      <section className="wofy-card mt-6" data-testid="share-leaderboard-empty">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
          <div className="overline">MOST SHARED · LAST {days} DAYS</div>
        </div>
        <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
          No shares yet this week. When someone opens a Social Pulse link on WhatsApp, X or Facebook, it lands here.
        </p>
      </section>
    );
  }

  return (
    <section className="wofy-card mt-6" data-testid="share-leaderboard">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
          <div className="overline">MOST SHARED · LAST {days} DAYS</div>
        </div>
        {summary && (
          <div className="flex items-center gap-3 text-xs font-mono" style={{ color: "var(--wofy-text-soft)" }}>
            <Share2 className="w-3.5 h-3.5" />
            <span data-testid="share-total"><b style={{ color: "var(--wofy-ink)" }}>{summary.total}</b> shares</span>
            {summary.by_network && Object.entries(summary.by_network).slice(0, 4).map(([n, c]) => (
              <span key={n} className="flex items-center gap-1" data-testid={`share-net-${n}`}>
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: NETWORK_COLORS[n] || "#999" }} />
                {NETWORK_LABELS[n] || n} · {c}
              </span>
            ))}
          </div>
        )}
      </div>

      <ol className="mt-5 space-y-3">
        {rows.map((it, idx) => {
          const linkFn = TYPE_LINKS[it.content_type];
          const url = linkFn ? linkFn(it) : null;
          const isPodium = idx < 3;
          return (
            <li
              key={`${it.content_type}-${it.id}`}
              data-testid={`share-row-${it.id}`}
              className="flex items-center gap-3 py-2 border-b last:border-b-0"
              style={{ borderColor: "var(--wofy-border)" }}
            >
              <div
                className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-mono text-xs font-bold"
                style={{
                  background: isPodium ? "var(--wofy-primary)" : "var(--wofy-surface)",
                  color: isPodium ? "#fff" : "var(--wofy-text-soft)",
                }}
              >
                {idx === 0 ? <Trophy className="w-4 h-4" /> : `#${idx + 1}`}
              </div>
              {it.image ? (
                <img src={it.image} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" loading="lazy" />
              ) : (
                <div className="w-12 h-12 rounded-lg shrink-0" style={{ background: "var(--wofy-surface)" }} />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase font-mono tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>
                  {TYPE_LABELS[it.content_type] || it.content_type}
                </div>
                <div className="font-semibold text-sm truncate">
                  {url ? (
                    <Link to={url} className="hover:underline">{it.title || "(untitled)"}</Link>
                  ) : (
                    it.title || "(untitled)"
                  )}
                </div>
                <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                  {Object.entries(it.networks || {}).slice(0, 6).map(([n, c]) => (
                    <span
                      key={n}
                      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono"
                      style={{
                        background: `${NETWORK_COLORS[n] || "#999"}15`,
                        color: NETWORK_COLORS[n] || "#333",
                      }}
                      data-testid={`share-net-chip-${it.id}-${n}`}
                    >
                      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: NETWORK_COLORS[n] || "#999" }} />
                      {NETWORK_LABELS[n] || n} · {c}
                    </span>
                  ))}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display text-2xl leading-none">{it.share_count}</div>
                <div className="text-[10px] uppercase font-mono tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>shares</div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
